import { useEffect, useState, useRef } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle, Undo2, Trash2 } from "lucide-react";

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function DataImportPanel() {
  const [type, setType] = useState("employees");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [fields, setFields] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [dateIdx, setDateIdx] = useState(null); // null | list of row indices missing a date (timesheets)
  const [dateVals, setDateVals] = useState({}); // { rowIndex: "YYYY-MM-DD" }
  const [dateAll, setDateAll] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const dateFixRef = useRef(null);
  const resultRef = useRef(null);
  const needsCompany = type !== "company";
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadSaved = async (cols) => {
    try {
      const { data } = await api.get(`/import/mapping?type=${type}${companyId ? `&company_id=${companyId}` : ""}`);
      const saved = data.mapping || {};
      const usable = Object.fromEntries(Object.entries(saved).filter(([, c]) => cols.includes(c)));
      return Object.keys(usable).length ? usable : null;
    } catch { return null; }
  };
  const saveMapping = async () => {
    try {
      await api.post("/import/mapping", { type, company_id: needsCompany ? companyId : null, mapping });
      toast.success("Mapping saved — future imports will reuse it");
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId(r.data[0].company_id); }); }, []);
  useEffect(() => {
    api.get(`/import/fields?type=${type}`).then((r) => setFields(r.data.fields || []));
    setColumns([]); setRows([]); setMapping({}); setResult(null); setFileName(""); setDateIdx(null); setDateVals({}); setDateAll("");
  }, [type]);

  useEffect(() => {
    if (dateIdx && dateFixRef.current) {
      dateFixRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [dateIdx]);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [result]);

  const autoMap = (cols, flds) => {
    const m = {};
    flds.forEach((f) => {
      const hit = cols.find((c) => norm(c) === norm(f.key) || norm(c) === norm(f.label) || norm(f.label).includes(norm(c)) || norm(c).includes(norm(f.key)));
      if (hit) m[f.key] = hit;
    });
    return m;
  };

  const processFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith(".xlsx"));
    if (!files.length) { toast.error("Please choose .xlsx file(s)"); return; }
    setBusy(true); setResult(null); setDateIdx(null); setDateVals({}); setDateAll("");
    try {
      let allCols = [];
      let allRows = [];
      let ok = 0, failed = [];
      for (const file of files) {
        try {
          const fd = new FormData(); fd.append("file", file);
          const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
          (data.columns || []).forEach((c) => { if (!allCols.includes(c)) allCols.push(c); });
          allRows = allRows.concat((data.rows || []).map((r) => ({ ...r, __file: file.name })));
          ok += 1;
        } catch { failed.push(file.name); }
      }
      setColumns(allCols); setRows(allRows);
      setFileName(files.length === 1 ? files[0].name : `${ok} files · ${allRows.length} row(s)`);
      const saved = await loadSaved(allCols);
      setMapping(saved || autoMap(allCols, fields));
      if (failed.length) toast.error(`Could not read: ${failed.join(", ")}`);
      if (!allRows.length) toast.info("No data rows found in the selected file(s).");
      else toast.success(`Loaded ${allRows.length} row(s) from ${ok} file${ok !== 1 ? "s" : ""}${saved ? " · applied your saved mapping" : ""} — review the mapping below`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    await processFiles(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    processFiles(e.dataTransfer.files);
  };

  const doRun = async (sendRows, sendMapping) => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/run", { type, company_id: companyId, mapping: sendMapping, rows: sendRows });
      setResult(data); setDateIdx(null); setDateVals({}); setDateAll("");
      const errByIdx = {}; (data.errors || []).forEach((e) => { errByIdx[e.row - 2] = e.error; });
      setRows((prev) => prev.map((r, i) => ({ ...r, __error: errByIdx[i] })));
      if (data.created > 0) toast.success(`Imported ${data.created} record(s)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) had issues`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    // Date is validated per-row for timesheets (below), so don't hard-block on its mapping here.
    const reqFields = fields.filter((f) => f.required && !(type === "timesheets" && f.key === "date"));
    const missing = reqFields.filter((f) => !mapping[f.key]);
    if (missing.length) { toast.error(`Map required fields: ${missing.map((f) => f.label).join(", ")}`); return; }
    if (needsCompany && !companyId) { toast.error("Pick a company"); return; }

    if (type === "timesheets") {
      const dateCol = mapping.date;
      const need = rows.map((_, i) => i).filter((i) => {
        const v = dateCol ? rows[i][dateCol] : null;
        return v === null || v === undefined || String(v).trim() === "";
      });
      if (need.length) {
        setDateIdx(need); setDateVals({}); setDateAll("");
        toast.info(`${need.length} row(s) have no date — enter a date for each before finalizing.`);
        return;
      }
    }
    doRun(rows, mapping);
  };

  const finalizeDates = () => {
    const dateKey = mapping.date || "__manual_date__";
    const finalMapping = mapping.date ? mapping : { ...mapping, date: "__manual_date__" };
    const finalRows = rows.map((r, i) => (dateVals[i] ? { ...r, [dateKey]: dateVals[i] } : r));
    const stillMissing = dateIdx.filter((i) => !dateVals[i]);
    if (stillMissing.length) toast.warning(`${stillMissing.length} row(s) still have no date and will be skipped.`);
    doRun(finalRows, finalMapping);
  };

  const undoImport = async () => {
    if (!result?.batch_id) return;
    setBusy(true);
    try {
      const { data } = await api.post("/import/undo", { batch_id: result.batch_id });
      toast.success(`Undone — ${data.deleted} record(s) removed`);
      setResult(null);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const deleteRow = (i) => {
    setRows(rows.filter((_, idx) => idx !== i));
  };

  const rowWho = (r) => {
    const em = mapping.match_email ? r[mapping.match_email] : "";
    const fn = mapping.match_first ? r[mapping.match_first] : "";
    const ln = mapping.match_last ? r[mapping.match_last] : "";
    return String(em || [fn, ln].filter(Boolean).join(" ") || "—");
  };

  return (
    <div className="space-y-6" data-testid="data-import-panel">
      <div className="border border-border bg-white p-5">
        <h2 className="font-bold tracking-tight mb-1">Import from Excel</h2>
        <p className="text-sm text-muted-foreground mb-4">Upload one or more .xlsx files, map their columns to the right fields, then import. The first row of each file must be column headers.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">What to import</label>
            <select data-testid="import-type-select" value={type} onChange={(e) => setType(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="employees">Employees</option>
              <option value="company">Companies</option>
              <option value="timesheets">Timesheet hours</option>
              <option value="timeoff">Time off</option>
            </select>
          </div>
          {needsCompany && (
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Into company</label>
              <select data-testid="import-company-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <label
          data-testid="import-dropzone"
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={onDrop}
          className={`mt-4 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-none py-8 cursor-pointer transition-colors ${dragOver ? "border-black bg-neutral-50" : "border-border hover:border-black"}`}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm">{dragOver ? "Drop your .xlsx file(s) here" : (fileName ? <span className="inline-flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> {fileName}</span> : "Drag & drop, or click to choose .xlsx file(s)")}</span>
          <span className="text-[11px] text-muted-foreground">You can select multiple files at once — rows are combined into one import</span>
          <input data-testid="import-file-input" type="file" accept=".xlsx" multiple onChange={onFile} className="hidden" disabled={busy} />
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-mono uppercase tracking-widest text-muted-foreground">Downloads:</span>
          <a data-testid="import-template-download" href={`${API}/api/import/export?type=${type}&template=true`} className="border border-black px-3 py-1.5 font-medium hover:bg-black hover:text-white transition-colors">Blank template (.xlsx)</a>
          <a data-testid="import-export-download" href={`${API}/api/import/export?type=${type}${needsCompany ? `&company_id=${companyId}` : ""}`} className={`border px-3 py-1.5 font-medium transition-colors ${needsCompany && !companyId ? "border-neutral-300 text-neutral-400 pointer-events-none" : "border-black hover:bg-black hover:text-white"}`}>Export current data (.xlsx)</a>
        </div>
      </div>

      {columns.length > 0 && (
        <div className="border border-border bg-white p-5" data-testid="import-mapping">
          <h3 className="font-bold tracking-tight mb-1">Map columns</h3>
          <p className="text-sm text-muted-foreground mb-4">Match each field to a column from your spreadsheet. Required fields are marked <span className="text-red-600">*</span>.</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {fields.map((f) => (
              <div key={f.key} className="flex items-center gap-2">
                <div className="w-1/2 text-sm">{f.label}{f.required && <span className="text-red-600"> *</span>}</div>
                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                <select data-testid={`map-${f.key}`} value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                  className={`flex-1 border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black ${f.required && !mapping[f.key] ? "border-red-400" : "border-input"}`}>
                  <option value="">— skip —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-5">
            {(() => {
              const cols = fields.filter((f) => mapping[f.key]);
              const anyErr = rows.some((r) => r.__error);
              const failCount = rows.filter((r) => r.__error).length;
              return (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className={`text-[10px] font-mono uppercase tracking-widest ${anyErr ? "text-red-600" : "text-muted-foreground"}`}>
                      {anyErr ? `Preview · ${failCount} failed (highlighted)` : `Preview (${rows.length} rows)`}
                    </div>
                    <div className="text-[10px] text-muted-foreground normal-case">Use the trash icon to drop rows you don't want to import</div>
                  </div>
                  <div className="overflow-x-auto border border-border max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0"><tr className="bg-neutral-50 text-left">
                        <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground">Row</th>
                        {cols.map((f) => <th key={f.key} className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground whitespace-nowrap">{f.label}</th>)}
                        {anyErr && <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-red-600 whitespace-nowrap">Problem</th>}
                        <th className="px-2 py-1.5"></th>
                      </tr></thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} data-testid={r.__error ? `import-error-row-${i}` : undefined} className={`border-t ${r.__error ? "bg-red-50 border-red-200" : "border-border"}`}>
                            <td className={`px-2 py-1.5 ${r.__error ? "text-red-700 font-medium" : "text-muted-foreground"}`}>{i + 2}</td>
                            {cols.map((f) => <td key={f.key} className={`px-2 py-1.5 whitespace-nowrap ${r.__error ? "text-red-800" : ""}`}>{String(r[mapping[f.key]] ?? "")}</td>)}
                            {anyErr && <td className="px-2 py-1.5 text-red-700 whitespace-nowrap">{r.__error || ""}</td>}
                            <td className="px-2 py-1.5 text-right">
                              <button data-testid={`import-delete-row-${i}`} onClick={() => deleteRow(i)} title="Remove this row from the import" className="text-muted-foreground hover:text-red-600 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button data-testid="import-run-button" onClick={runImport} disabled={busy || rows.length === 0} className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Importing…" : `Import ${rows.length} row(s)`}
            </button>
            <button data-testid="import-save-mapping" onClick={saveMapping} className="border border-black px-4 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors">Save mapping</button>
          </div>
        </div>
      )}

      {dateIdx && (
        <div ref={dateFixRef} className="border-2 border-amber-400 bg-amber-50 p-5 scroll-mt-6 shadow-lg" data-testid="import-date-fixup">
          <div className="flex items-center gap-2 text-amber-800 mb-1">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="font-bold tracking-tight">{dateIdx.length} row(s) are missing a date</h3>
          </div>
          <p className="text-sm text-amber-900/80 mb-4">Enter the work date for each timesheet row below, then finalize the upload. Rows left blank will be skipped.</p>
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-900/70">Set all to</label>
              <input type="date" data-testid="import-date-all" value={dateAll} onChange={(e) => setDateAll(e.target.value)} className="mt-1 block border border-amber-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <button data-testid="import-date-apply-all" disabled={!dateAll} onClick={() => { const nv = { ...dateVals }; dateIdx.forEach((i) => { nv[i] = dateAll; }); setDateVals(nv); }} className="border border-amber-600 text-amber-800 px-3 py-1.5 text-sm font-medium hover:bg-amber-600 hover:text-white transition-colors disabled:opacity-40">Apply to all</button>
          </div>
          <div className="overflow-x-auto border border-amber-200 bg-white max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0"><tr className="bg-neutral-50 text-left">
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground">Row</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground">Employee</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground">Hours</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[9px] text-muted-foreground">Date <span className="text-red-600">*</span></th>
              </tr></thead>
              <tbody>
                {dateIdx.map((i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5 text-muted-foreground">{i + 2}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{rowWho(rows[i])}</td>
                    <td className="px-2 py-1.5">{mapping.hours ? String(rows[i][mapping.hours] ?? "") : ""}</td>
                    <td className="px-2 py-1.5">
                      <input type="date" data-testid={`import-date-row-${i}`} value={dateVals[i] || ""} onChange={(e) => setDateVals({ ...dateVals, [i]: e.target.value })} className={`border px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 ${dateVals[i] ? "border-input" : "border-red-400"}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="import-date-finalize" onClick={finalizeDates} disabled={busy} className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Importing…" : "Finalize import"}
            </button>
            <button data-testid="import-date-cancel" onClick={() => { setDateIdx(null); setDateVals({}); setDateAll(""); }} className="border border-border px-4 py-3 text-sm font-medium hover:border-black transition-colors">Cancel</button>
            <span className="text-xs text-amber-900/70">{dateIdx.filter((i) => dateVals[i]).length} of {dateIdx.length} filled</span>
          </div>
        </div>
      )}

      {result && (
        <div ref={resultRef} className="border border-border bg-white p-5 scroll-mt-6" data-testid="import-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-bold" data-testid="import-created-count">{result.created} record(s) created</span></div>
            {result.batch_id && result.batch_count > 0 && (
              <button data-testid="import-undo-button" onClick={undoImport} disabled={busy} className="inline-flex items-center gap-2 border border-red-500 text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50">
                <Undo2 className="h-4 w-4" /> {busy ? "Undoing…" : `Undo last import (${result.batch_count})`}
              </button>
            )}
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-amber-700 mb-2"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-medium">{result.errors.length} row(s) skipped — shown highlighted in the preview above</span></div>
              <ul className="text-xs text-muted-foreground space-y-0.5 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
